import isEqual from 'lodash/isEqual'

import {
  reconcileZoneSnapshot,
  type ZoneParams,
  zoneParamsSchema,
} from './zoneAnalysisApi'

export interface PersistedZoneSnapshot {
  readonly zones: readonly ZoneParams[]
  readonly rawZones: unknown
  readonly justification: string | undefined
}

function mergeEditedValue(
  rawValue: unknown,
  previousValue: unknown,
  nextValue: unknown
): unknown {
  if (isEqual(previousValue, nextValue)) return rawValue
  if (!isRecord(previousValue) || !isRecord(nextValue)) return nextValue

  const rawRecord = isRecord(rawValue) ? { ...rawValue } : {}
  for (const key of new Set([
    ...Object.keys(previousValue),
    ...Object.keys(nextValue),
  ])) {
    if (isEqual(previousValue[key], nextValue[key])) continue
    if (!Object.hasOwn(nextValue, key) || nextValue[key] === undefined) {
      delete rawRecord[key]
    } else {
      rawRecord[key] = mergeEditedValue(
        rawRecord[key],
        previousValue[key],
        nextValue[key]
      )
    }
  }
  return rawRecord
}

function prepareZoneForSave(
  rawZone: unknown,
  previousZone: ZoneParams | undefined,
  nextZone: ZoneParams
): Record<string, unknown> {
  const prepared =
    rawZone !== undefined && previousZone
      ? mergeEditedValue(rawZone, previousZone, nextZone)
      : nextZone
  const result = isRecord(prepared) ? { ...prepared } : {}
  result.id = nextZone.id
  for (const key of ['drawing_bounds', 'ai_initial_bounds']) {
    const bounds = result[key]
    if (bounds !== undefined && !Array.isArray(bounds)) result[key] = [bounds]
  }
  return result
}

export function prepareZonesForSave({
  rawZones,
  previousZones,
  latestZones,
  proposedZones,
}: {
  rawZones: unknown
  previousZones: readonly ZoneParams[]
  latestZones: readonly ZoneParams[]
  proposedZones: readonly ZoneParams[]
}): unknown[] {
  const raw = Array.isArray(rawZones) ? rawZones : []
  const latestById = new Map(latestZones.map((zone, index) => [zone.id, index]))
  const previousById = new Map(previousZones.map((zone) => [zone.id, zone]))

  return proposedZones.map((zone) => {
    const latestIndex = latestById.get(zone.id)
    return prepareZoneForSave(
      latestIndex === undefined ? undefined : raw[latestIndex],
      previousById.get(zone.id),
      zone
    )
  })
}

export function matchesPersistedZoneSnapshot(
  snapshot: PersistedZoneSnapshot,
  candidate: PersistedZoneSnapshot
): boolean {
  return (
    snapshot.justification === candidate.justification &&
    isEqual(snapshot.rawZones, candidate.rawZones) &&
    isEqual(
      snapshot.zones,
      reconcileZoneSnapshot(
        snapshot.rawZones,
        snapshot.zones,
        candidate.rawZones,
        candidate.zones
      ).zones
    )
  )
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function hasDuplicateStoredZoneIds(rawZones: unknown): boolean {
  if (!Array.isArray(rawZones)) return false
  const ids = rawZones
    .filter(isRecord)
    .map((zone) => zone.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0)
  return new Set(ids).size !== ids.length
}

export function hasEmptyStoredZoneId(rawZones: unknown): boolean {
  return (
    Array.isArray(rawZones) &&
    rawZones.some((zone) => isRecord(zone) && zone.id === '')
  )
}

export function replaceSavedZone(
  fieldExists: boolean,
  rawZones: unknown,
  selectedZone: ZoneParams,
  wasPreviouslySaved = false,
  baselineZones?: readonly ZoneParams[]
): unknown[] {
  if (selectedZone.ai_suggested) {
    throw new Error('Confirm this AI-suggested zone before saving it.')
  }
  if (!fieldExists) {
    if (wasPreviouslySaved) {
      throw new Error(
        'The selected saved zone is missing from the ticket. Refresh and review the zone list before saving.'
      )
    }
    return [prepareZoneForSave(undefined, undefined, selectedZone)]
  }

  if (!Array.isArray(rawZones)) {
    throw new Error(
      'Saved zone data is invalid. Reload the ticket before saving.'
    )
  }

  const validated = zoneParamsSchema.array().safeParse(rawZones)
  if (!validated.success) {
    throw new Error(
      'Saved zone data is invalid. Reload the ticket and correct the zone data before saving.'
    )
  }

  const ids = rawZones.map((raw) => (isRecord(raw) ? raw.id : undefined))
  if (ids.some((id) => typeof id !== 'string' || id.length === 0)) {
    throw new Error(
      'A saved zone is missing its stored ID. Use page Save to persist all page changes and write stable zone IDs, then retry Save zone.'
    )
  }
  if (new Set(ids).size !== ids.length) {
    throw new Error(
      'Saved zones contain duplicate IDs. Have the stored IDs repaired before using Save zone.'
    )
  }

  const selectedIndex = validated.data.findIndex(
    (zone) => zone.id === selectedZone.id
  )
  if (selectedIndex < 0) {
    if (wasPreviouslySaved) {
      throw new Error(
        'The selected saved zone is no longer present on the ticket. Refresh and review the zone list before saving.'
      )
    }
    return [...rawZones, prepareZoneForSave(undefined, undefined, selectedZone)]
  }

  return rawZones.map((raw, index) =>
    index === selectedIndex
      ? prepareZoneForSave(
          raw,
          baselineZones?.find((zone) => zone.id === selectedZone.id) ??
            validated.data[selectedIndex],
          selectedZone
        )
      : raw
  )
}

export function reconcileZoneDrafts(
  comparisonBaseline: readonly ZoneParams[],
  currentDraft: readonly ZoneParams[],
  nextSaved: readonly ZoneParams[]
): ZoneParams[] {
  const baselineById = new Map(
    comparisonBaseline.map((zone) => [zone.id, zone])
  )
  const draftById = new Map(currentDraft.map((zone) => [zone.id, zone]))
  const nextIds = new Set(nextSaved.map((zone) => zone.id))
  const nextDraft: ZoneParams[] = []

  const keepDraft = (zone: ZoneParams | undefined, id: string) =>
    zone?.ai_suggested && nextIds.has(id)
      ? false
      : !isEqual(zone, baselineById.get(id))

  for (const saved of nextSaved) {
    const draft = draftById.get(saved.id)
    if (keepDraft(draft, saved.id)) {
      if (draft) nextDraft.push(draft)
    } else {
      nextDraft.push(saved)
    }
  }

  for (const draft of currentDraft) {
    if (!nextIds.has(draft.id) && keepDraft(draft, draft.id)) {
      nextDraft.push(draft)
    }
  }

  return nextDraft
}
